import { StockLocationResponse } from '@/api/profile'
import { CartBadge } from '@/app/features/cart/ui/cart-badge/cart-badge'
import { AppService } from '@/app/shared/services/app.service'
import { UserService } from '@/app/shared/services/user.service'
import { StockLocationPicker } from '@/app/v2/app/pickers/stock-location-picker/stock-location-picker'
import { SettingsDialog } from '@/app/v2/shared/components/dialogs/settings-dialog/settings-dialog'
import { BreakpointObserver } from '@angular/cdk/layout'
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay'
import { UpperCasePipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, inject, input, linkedSignal, signal } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { form, FormField } from '@angular/forms/signals'
import { NavigationEnd, Router, RouterLink, RouterLinkActive, UrlTree } from '@angular/router'
import { MsalService } from '@azure/msal-angular'
import { filter, map } from 'rxjs'

export type NavLink = {
  name: string
  /** Libellé court affiché sous l'icône en mode réduit (repli sur `name` si absent). */
  shortName?: string
  icon?: string
  url?: string | readonly any[] | UrlTree | null | undefined
  nestedLinks?: NavLink[]
  badge?: number | null
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-sidenav',
  imports: [
    RouterLink,
    RouterLinkActive,
    UpperCasePipe,
    CdkConnectedOverlay,
    CdkOverlayOrigin,
    CartBadge,
    FormField,
    StockLocationPicker,
  ],
  templateUrl: './sidenav.html',
})
export class SideNav {
  user = inject(UserService)
  msal = inject(MsalService)
  app = inject(AppService)
  private readonly router = inject(Router)
  private readonly breakpointObserver = inject(BreakpointObserver)

  home = input.required<string | readonly any[] | UrlTree | null | undefined>()
  links = input.required<NavLink[]>()

  // --- Collapse ---

  collapsed = this.app.sideNavCollapsed

  private readonly handset = toSignal(
    this.breakpointObserver.observe('(max-width: 767px)').pipe(map((state) => state.matches)),
    { initialValue: false },
  )

  /** Le rail se replie toujours sur petit écran, indépendamment de la préférence desktop. */
  readonly renderCollapsed = computed(() => this.collapsed() || this.handset())
  readonly collapseToggleVisible = computed(() => !this.handset())

  toggleCollapsed() {
    this.app.sideNavCollapsed.update((v) => !v)
  }

  // --- Nested Links ---

  // Chemin courant (sans query string), réactif à la navigation.
  private readonly currentPath = toSignal(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map(() => this.router.url.split('?')[0]),
    ),
    { initialValue: this.router.url.split('?')[0] },
  )

  // Index du groupe contenant la route active (-1 si aucun).
  private readonly activeGroupIndex = computed(() => {
    const path = this.currentPath()
    return this.links().findIndex((l) =>
      l.nestedLinks?.some(
        (n) => typeof n.url === 'string' && (path === '/' + n.url || path.startsWith('/' + n.url + '/')),
      ),
    )
  })

  // Groupes OUVERTS — PLUSIEURS à la fois (fini l'accordéon qui repliait les autres). Le groupe de
  // la route active est ajouté à l'ouverture ET à chaque navigation, SANS refermer ceux déjà ouverts
  // (linkedSignal : on repart de la valeur précédente et on ajoute l'actif). L'utilisateur pilote le
  // reste via toggle / tout déplier / tout replier.
  openIndices = linkedSignal<number, Set<number>>({
    source: () => this.activeGroupIndex(),
    computation: (activeIndex, previous) => {
      const next = new Set(previous?.value ?? [])
      if (activeIndex >= 0) next.add(activeIndex)
      return next
    },
  })

  isOpen(i: number): boolean {
    return this.openIndices().has(i)
  }

  toggle(i: number) {
    this.openIndices.update((set) => {
      const next = new Set(set)
      if (next.has(i)) next.delete(i)
      else next.add(i)
      return next
    })
  }

  /** Index des groupes (avec sous-menus) — base de « tout déplier ». */
  private readonly groupIndices = computed(() =>
    this.links()
      .map((l, i) => (l.nestedLinks?.length ? i : -1))
      .filter((i) => i >= 0),
  )

  readonly hasGroups = computed(() => this.groupIndices().length > 0)
  readonly allExpanded = computed(
    () => this.groupIndices().length > 0 && this.groupIndices().every((i) => this.openIndices().has(i)),
  )

  toggleAll() {
    this.openIndices.set(this.allExpanded() ? new Set<number>() : new Set(this.groupIndices()))
  }

  // --- User Menu ---

  readonly userMenuPositions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetX: 0,
    },
  ]

  userMenuOpen = signal(false)

  initials() {
    const user = this.user.user()
    if (!user) return ''
    const f = user.firstName?.[0] ?? ''
    const l = user.lastName?.[0] ?? ''
    return (f + l).toUpperCase()
  }

  logout() {
    this.msal.logoutRedirect()
  }

  openUserMenu() {
    this.userMenuOpen.set(true)
  }

  closeUserMenu() {
    this.userMenuOpen.set(false)
  }

  toggleUserMenu() {
    this.userMenuOpen.update((v) => !v)
  }

  openSettings() {
    this.userMenuOpen.set(false)
    this.app.openComponentDialog(SettingsDialog)
  }

  stockLocation = linkedSignal<StockLocationResponse | null, StockLocationResponse | null>({
    source: () => this.app.stockLocation(),
    computation: (stockLocation) => stockLocation,
  })

  stockLocationForm = form(this.stockLocation)

  onStockLocationChanged(stockLocation: StockLocationResponse | null) {
    console.log('Selected stock location:', stockLocation)
    this.app.setStockLocation(stockLocation)
  }
}
